import {
  Box,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'

import AssistantAvatar from '~/features/chatbot/components/assistantAvatar'
import {
  getMessageColors,
  safeHttpHref,
} from '~/features/chatbot/components/message/utils'
import { EMessageSpeaker } from '~/models/features'
import { ETestId } from '~/models/tests'
import { store } from '~/store'
import rem from '~/utils/ui'

// Supported Markdown subset for AI-generated chat messages (see
// heyLibbyChat/services/chat-service/src/ai/systemPrompt.ts — the contract
// given to the AI): paragraphs, line breaks, bold, italic, bulleted/numbered
// lists, and links. Everything else (headings, code blocks, tables, images,
// raw HTML) is intentionally excluded from allowedElements below — images in
// particular are a deliberate exclusion, not an oversight, since remote
// image loading from AI-controlled content is a tracking-pixel/SSRF-style
// risk (see this ticket's design doc, Security section).
const MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => (
    <Typography variant="body1" component="div" sx={{ m: 0 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => {
    const safe = safeHttpHref(href)

    if (!safe) {
      return <Box component="span">{children}</Box>
    }

    return (
      <MuiLink
        href={safe}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ wordBreak: 'break-all' }}
      >
        {children}
      </MuiLink>
    )
  },
  ul: ({ children }) => (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1">
      {children}
    </Typography>
  ),
  br: () => <br />,
}

type TProps = {
  speaker: EMessageSpeaker
  message: string
}

function Message({ speaker, message }: TProps) {
  const [chatbotLayout, chatbotAgent] = store((state) => [
    state.chatbot.layout,
    state.chatbot.agent,
  ])
  const theme = useTheme()
  const { userColor, botColor, userBackgroundColor, botBackgroundColor } =
    getMessageColors(chatbotLayout, theme)

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      useFlexGap
      data-testid={
        speaker === EMessageSpeaker.user
          ? ETestId.ChatMessageUser
          : ETestId.ChatMessageAssistant
      }
      sx={{
        alignSelf: speaker === EMessageSpeaker.user ? 'flex-end' : 'flex-start',
        maxWidth: '70%',
      }}
    >
      {speaker === EMessageSpeaker.assistant && (
        <AssistantAvatar
          chatbotImgURL={chatbotAgent.chatbotImgURL}
          variant="small"
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          '&.MuiPaper-root': {
            borderRadius: rem(16),
          },
          p: 1,
          width: 'fit-content',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          color: speaker === EMessageSpeaker.user ? userColor : botColor,
          backgroundColor:
            speaker === EMessageSpeaker.user
              ? userBackgroundColor
              : botBackgroundColor,
        }}
      >
        <ReactMarkdown
          allowedElements={['p', 'a', 'strong', 'em', 'ul', 'ol', 'li', 'br']}
          unwrapDisallowed
          skipHtml
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={MARKDOWN_COMPONENTS}
        >
          {message}
        </ReactMarkdown>
      </Paper>
    </Stack>
  )
}

export default Message
